import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Layout from '../components/layout/Layout';
import Seo from '../components/common/Seo';
import { getMenuSection } from '../data/menuLookup';
import { SITE_URL, addressLine, business, hoursDisplay } from '../data/business';

const easing: [number, number, number, number] = [0.25, 0.1, 0.25, 1];

/**
 * Landing page for the Street Food section — the part of the menu that is
 * genuinely unusual for a Seattle Thai restaurant. Dishes are read live from
 * the menu, so this page can never disagree with /menu.
 */
const StreetFoodPage = () => {
  const items = getMenuSection('Street Food');

  const faqs = [
    {
      question: 'What is Thai street food?',
      answer:
        'Thai street food is the food sold from carts, stalls and open-fronted shophouses across Thailand — grilled skewers, papaya salad pounded to order, rice plates topped with a fried egg, noodle soups. It is cooked fast, eaten fast, and seasoned harder than most restaurant Thai food in the United States.',
    },
    {
      question: 'Which street food dishes does Thaprachan serve?',
      answer: `Our Street Food section has ${items.length} dishes, including ${items
        .slice(0, 4)
        .map((i) => i.name)
        .join(', ')} and more. The full list is on this page and on our menu.`,
    },
    {
      question: 'Where can I get Thai street food in Seattle?',
      answer:
        'Thaprachan Thai Cuisine serves a dedicated Street Food menu at 805 NE 65th St, Seattle, WA 98115, in north Seattle about a 10-minute drive from the University of Washington. Order online or call (206) 523-3938.',
    },
    {
      question: 'Can I order street food dishes for delivery?',
      answer:
        'Yes — the Street Food section is available through our online ordering, the same as the rest of the menu.',
    },
  ];

  const schemas = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Thai Street Food',
          item: `${SITE_URL}/thai-street-food-seattle`,
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqs.map((f) => ({
        '@type': 'Question',
        name: f.question,
        acceptedAnswer: { '@type': 'Answer', text: f.answer },
      })),
    },
  ];

  return (
    <>
      <Seo
        title="Best Thai Street Food in Seattle | Thaprachan Thai"
        description="A dedicated Thai street food menu in north Seattle — som tum tard, pork belly skewers, pad kraprow with fried egg, moo nam tok and more. 805 NE 65th St."
        path="/thai-street-food-seattle"
        schemas={schemas}
      />

      <Layout>
        <section className="relative bg-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="/assets/images/Hero Background Photos/optimized/IMG_8254.webp"
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/80" />
          </div>

          <div className="relative z-10 container mx-auto px-6 lg:px-8 py-20 md:py-28">
            <nav aria-label="Breadcrumb" className="mb-6">
              <ol className="flex flex-wrap items-center gap-2 text-xs font-body tracking-widest uppercase text-white/60">
                <li><Link to="/" className="hover:text-primary transition-colors">Home</Link></li>
                <li aria-hidden="true">/</li>
                <li className="text-primary">Thai Street Food</li>
              </ol>
            </nav>

            <motion.p
              className="text-sm tracking-widest uppercase text-primary/90 font-medium font-body mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: easing }}
            >
              A section most Thai menus don&rsquo;t have
            </motion.p>
            <motion.h1
              className="font-heading text-4xl sm:text-5xl md:text-6xl text-white leading-tight max-w-4xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.12, ease: easing }}
            >
              Thai Street Food in Seattle
            </motion.h1>
            <motion.p
              className="font-body text-lg text-white/85 max-w-2xl mt-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.25 }}
            >
              {items.length} dishes from the carts and night markets of Thailand — grilled
              skewers, pounded salads, rice plates under a fried egg — on NE 65th St.
            </motion.p>
            <motion.div
              className="flex flex-wrap gap-4 mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.35 }}
            >
              <a
                href={business.orderUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-track="order_click"
                className="btn-primary rounded-sm"
              >
                Order Online →
              </a>
              <a
                href={business.telephoneHref}
                data-track="call_click"
                className="px-8 py-3 border border-white/50 text-white font-body font-medium rounded-sm hover:bg-white/10 transition-colors"
              >
                Call {business.telephoneDisplay}
              </a>
            </motion.div>
          </div>
        </section>

        <div className="bg-background bg-paper-texture">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
            <div className="max-w-3xl">
              <p className="text-sm tracking-widest uppercase text-primary-ink font-medium font-body mb-4">
                What this section is
              </p>
              <h2 className="font-heading text-3xl md:text-4xl text-secondary">
                The best Thai street food in Seattle
              </h2>
              <div className="mt-3 h-0.5 w-12 bg-primary" />
              <p className="font-body text-text/90 leading-relaxed mt-6">
                Most Thai restaurants in the United States build a menu out of noodles, curries
                and fried rice. Street food is a different category of cooking — the food Thai
                people actually eat standing up, from carts and open-fronted shops: papaya salad
                pounded to order, pork belly grilled over charcoal, minced meat and basil over
                rice with a runny fried egg on top.
              </p>
              <p className="font-body text-text/90 leading-relaxed mt-4">
                Thaprachan runs it as its own section of {items.length} dishes, separate from the
                noodle and curry pages — which is why we will happily claim the best Thai street
                food in Seattle. If you have been hunting for som tum tard or moo nam tok in north
                Seattle rather than another plate of pad thai, this is the part of the menu to read.
              </p>
            </div>

            <section className="mt-14">
              <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                The Street Food menu
              </h2>
              <div className="mt-3 h-0.5 w-12 bg-primary" />

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-12 md:gap-x-20">
                {items.map((item, i) => (
                  <motion.div
                    key={item.id}
                    className="py-5 border-b border-neutral-warm"
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.2 }}
                    transition={{ duration: 0.5, delay: Math.min(i * 0.03, 0.3), ease: 'easeOut' }}
                  >
                    <h3 className="font-heading text-base text-secondary mb-1">{item.name}</h3>
                    {item.description && (
                      <p className="font-body text-sm text-text/80 leading-relaxed">
                        {item.description}
                      </p>
                    )}
                  </motion.div>
                ))}
              </div>

              <p className="font-body text-sm text-text/70 mt-8">
                Prices are on the{' '}
                <Link to="/menu" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                  full menu
                </Link>
                , alongside our{' '}
                <Link to="/dishes" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                  signature dishes
                </Link>
                .
              </p>
            </section>

            <div className="max-w-3xl">
              <section className="mt-16">
                <h2 className="font-heading text-2xl md:text-3xl text-secondary">
                  Thai street food questions
                </h2>
                <div className="mt-3 h-0.5 w-12 bg-primary" />
                <dl className="mt-6 divide-y divide-neutral-warm border-t border-neutral-warm">
                  {faqs.map((faq) => (
                    <div key={faq.question} className="py-5">
                      <dt className="font-heading text-lg text-secondary mb-2">{faq.question}</dt>
                      <dd className="font-body text-text/85 leading-relaxed">{faq.answer}</dd>
                    </div>
                  ))}
                </dl>
              </section>

              <section className="mt-16">
                <h2 className="font-heading text-2xl md:text-3xl text-secondary">Find us</h2>
                <div className="mt-3 h-0.5 w-12 bg-primary" />
                <div className="mt-6 grid gap-6 sm:grid-cols-2">
                  <div className="bg-white rounded-sm shadow-elegant p-6">
                    <address className="not-italic font-body text-sm text-text/85 leading-relaxed">
                      {addressLine}
                      <br />
                      <a href={business.telephoneHref} data-track="call_click" className="text-primary-ink hover:text-secondary">
                        {business.telephoneDisplay}
                      </a>
                    </address>
                    <p className="font-body text-sm text-text/70 mt-3">
                      About a 10-minute drive from the{' '}
                      <Link to="/near-uw" className="text-primary-ink underline underline-offset-2 hover:text-secondary">
                        University of Washington
                      </Link>
                      .
                    </p>
                  </div>
                  <div className="bg-white rounded-sm shadow-elegant p-6">
                    <h3 className="font-heading text-lg text-secondary mb-3">Hours</h3>
                    <ul className="font-body text-sm text-text/85 space-y-1">
                      {hoursDisplay.map((h) => (
                        <li key={h.day} className="flex justify-between gap-4">
                          <span>{h.day}</span>
                          <span className="text-text/70 tabular-nums">{h.time}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default StreetFoodPage;
